import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Facebook, Lock, ChevronRight } from 'lucide-react';

const Footer = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    "name": "MCA CAR",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "32 Rue Marcellin Berthelot",
      "addressLocality": "Quarouble",
      "postalCode": "59243",
      "addressCountry": "FR"
    },
    "telephone": "+33613851946",
    "openingHours": ["Mo-Fr 09:00-12:00", "Mo-Fr 14:00-18:00"],
    "geo": { "@type": "GeoCoordinates", "latitude": 50.3833, "longitude": 3.6000 },
    "areaServed": ["Quarouble", "Onnaing", "Valenciennes", "Fresnes-sur-Escaut", "Condé-sur-l'Escaut"],
    "priceRange": "$$",
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "10" }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <footer style={{ background: 'var(--bg-primary)' }}>
      {/* CTA Band */}
      <div className="border-t border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl lg:text-2xl font-bold text-white tracking-tight">Besoin d'un devis ou d'un rendez-vous ?</h3>
            <p className="text-zinc-500 text-sm mt-1">Réponse sous 24h. Devis gratuit et sans engagement.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-[#DC2626] text-white font-semibold text-sm transition-all duration-200 hover:bg-[#B91C1C]">
              Demander un devis <ChevronRight size={16} />
            </Link>
            <a href="tel:+33613851946" className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white font-semibold text-sm transition-all duration-200 hover:bg-white/5">
              <Phone size={16} /> Appeler
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#DC2626] text-white font-extrabold text-lg px-3 py-1.5">
                MCA
              </div>
              <div>
                <div className="font-bold text-white text-lg">MCA CAR</div>
                <div className="text-[10px] text-zinc-600 tracking-widest uppercase" style={{ fontFamily: 'var(--font-mono)' }}>Garage & Vente Auto</div>
              </div>
            </div>
            <p className="text-zinc-500 text-sm leading-relaxed mb-6">
              Votre spécialiste automobile à Quarouble. Réparation, vente de véhicules d'occasion et pièces détachées depuis plus de 10 ans.
            </p>
            <a
              href="https://www.facebook.com/share/1CBFASZRBu/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white transition-colors"
              data-testid="footer-facebook"
            >
              <Facebook size={16} /> Suivez-nous sur Facebook
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-[0.15em] mb-6" style={{ fontFamily: 'var(--font-mono)' }}>Navigation</h3>
            <ul className="space-y-3">
              {[
                { to: '/', label: 'Accueil' },
                { to: '/vehicules', label: 'Véhicules à vendre' },
                { to: '/services', label: 'Nos services' },
                { to: '/a-propos', label: 'À propos' },
                { to: '/contact', label: 'Contact' },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-zinc-500 hover:text-white transition-colors duration-200">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-[0.15em] mb-6" style={{ fontFamily: 'var(--font-mono)' }}>Services</h3>
            <ul className="space-y-3">
              {['Réparation automobile', 'Diagnostic automobile', 'Contrôle technique', 'Vente de pièces auto', 'Plaques d\'immatriculation', 'Accessoires automobile'].map(s => (
                <li key={s} className="text-sm text-zinc-500">{s}</li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-[0.15em] mb-6" style={{ fontFamily: 'var(--font-mono)' }}>Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="text-[#DC2626] flex-shrink-0 mt-0.5" size={16} />
                <span className="text-sm text-zinc-500">
                  32 Rue Marcellin Berthelot<br />59243 Quarouble
                </span>
              </li>
              <li>
                <a href="tel:+33613851946" className="flex items-center gap-3 text-sm text-zinc-500 hover:text-white transition-colors">
                  <Phone className="text-[#DC2626]" size={16} />
                  06 13 85 19 46
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="text-[#DC2626] flex-shrink-0 mt-0.5" size={16} />
                <span className="text-sm text-zinc-500">
                  Lun - Ven: 9h - 12h / 14h - 18h<br />
                  Sam: Sur rendez-vous
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-zinc-600 text-xs">
              © {new Date().getFullYear()} MCA CAR. Tous droits réservés.
            </p>
            <Link to="/admin" className="text-zinc-700 hover:text-zinc-400 flex items-center gap-1 transition-colors text-xs" data-testid="admin-link">
              <Lock size={11} /> Administration
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
